import { MutableRefObject, useEffect, useMemo } from "react";

import { Box, Flex, SimpleGrid } from "@mantine/core";
import { UseFormReturnType } from "@mantine/form";
import moment from "moment";
import { useTranslation } from "react-i18next";

import {
  Checkbox,
  DatePicker,
  GenderRadioGroup,
  InputLabel,
  Select,
  Text,
  TextInput,
  TimePicker,
} from "components";

import { convertToTimeString } from "components/time";
import { BankCreditOptionsResponse, BankCreditRequestForm, BankCreditResponse } from "types";
import { AppUtils } from "utils";

interface CreditRequestPersonalInfoProps {
  credit: BankCreditResponse;
  form: UseFormReturnType<BankCreditRequestForm>;
  options: BankCreditOptionsResponse;
  visibleFields: string[];
  readOnly?: boolean;
  updateCredit: (item: Partial<BankCreditRequestForm>) => Promise<void>;
  revalidateEmploymentDatesRef?: MutableRefObject<
    ((newBirthDate?: Date | undefined | null, newVisaFrom?: Date | undefined | null) => void) | null
  >;
}

export const BankCreditRequestPersonalInfo = ({
  credit,
  form,
  options,
  visibleFields,
  readOnly = false,
  updateCredit,
  revalidateEmploymentDatesRef,
}: CreditRequestPersonalInfoProps) => {
  const { t } = useTranslation();

  const nationalities = useMemo(
    () => AppUtils.getOrderedCountryList(options["nationality"], t),
    [options, t],
  );

  const creditLanguageOptions = useMemo(
    () =>
      options["language"]
        .filter((e) => e === "DE" || e === "FR" || e === "IT")
        .map((el) => ({
          value: el,
          label: t(`bankCredit.creditLanguage.options.${el}`),
        })),
    [options, t],
  );

  useEffect(() => {
    // Only save calculated default if backend doesn't have a value yet
    if (!credit.creditLanguage && !readOnly && form.values.creditLanguage) {
      updateCredit({
        creditLanguage: form.values.creditLanguage,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const nationality = form.getValues().nationality;
  const canton = form.getValues().canton;

  useEffect(() => {
    // Update creditLanguage whenever nationality or canton changes
    if (!readOnly && (nationality || canton)) {
      const newLanguage = AppUtils.getCreditLanguage(nationality, canton);

      if (newLanguage !== form.values.creditLanguage) {
        form.setFieldValue("creditLanguage", newLanguage);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nationality, canton, readOnly]);

  return (
    <Box>
      <GenderRadioGroup
        fieldName="gender"
        form={form}
        disabled={readOnly}
        genderOptions={options["gender"]}
      />

      <SimpleGrid cols={4} mt={"sm"}>
        <TextInput
          label={t("user.firstName.label")}
          key={form.key("user.firstName")}
          {...form.getInputProps("user.firstName")}
          disabled={true}
          required={true}
        />
        <TextInput
          label={t("user.lastName.label")}
          key={form.key("user.lastName")}
          {...form.getInputProps("user.lastName")}
          disabled={true}
          required={true}
        />
        <DatePicker
          label={t("user.birthDate.label")}
          placeholder={t("user.birthDate.placeholder")}
          key={form.key("birthDate")}
          {...form.getInputProps("birthDate")}
          onChange={(value: Date | null) => {
            form.setFieldValue("birthDate", value || undefined);
            revalidateEmploymentDatesRef?.current?.(value || undefined, null);
          }}
          minDate={moment().subtract(65, "years")}
          maxDate={moment().subtract(18, "years")}
          required={true}
          disabled={readOnly}
        />
      </SimpleGrid>
      <SimpleGrid cols={4} mt={"sm"}>
        <TextInput
          label={t("user.phone.label")}
          key={form.key("user.phone")}
          {...form.getInputProps("user.phone")}
          disabled={true}
          required={true}
        />
        <TextInput
          label={t("user.email.label")}
          key={form.key("user.email")}
          {...form.getInputProps("user.email")}
          disabled={true}
          required={true}
        />
        <Box>
          <InputLabel label={t("credit.workdayAvailability.label")} />
          <Flex align={"center"} justify={"space-evenly"}>
            <Text size={"sm"} c={"var(--mantine-color-gray-6)"}>
              {t("credit.workdayAvailability.from")}
            </Text>
            <TimePicker
              key={form.key("workdayAvailabilityFrom")}
              {...form.getInputProps("workdayAvailabilityFrom")}
              min={options["workdayAvailability"]?.min}
              max={options["workdayAvailability"]?.max}
              w={"30%"}
              disabled={readOnly}
            />
            <Text size={"sm"} c={"var(--mantine-color-gray-6)"}>
              {t("credit.workdayAvailability.to")}
            </Text>
            <TimePicker
              key={form.key("workdayAvailabilityTo")}
              {...form.getInputProps("workdayAvailabilityTo")}
              min={options["workdayAvailability"]?.min}
              max={options["workdayAvailability"]?.max}
              w={"30%"}
              disabled={readOnly}
            />
          </Flex>
          <Checkbox
            key={form.key("saturdayAvailability")}
            label={t("credit.saturdayAvailability.label")}
            {...form.getInputProps("saturdayAvailability", { type: "checkbox" })}
            customOnClick={() => {
              form.setFieldValue(
                "saturdayAvailabilityFrom",
                convertToTimeString(options["saturdayAvailability"]?.min || 0),
              );
              form.setFieldValue(
                "saturdayAvailabilityTo",
                convertToTimeString(options["saturdayAvailability"]?.max || 0),
              );
            }}
            disabled={readOnly}
          />
        </Box>

        {visibleFields.includes("saturdayAvailabilityFrom") &&
          visibleFields.includes("saturdayAvailabilityTo") && (
            <Box>
              <InputLabel label={t("credit.saturdayAvailability.label")} />
              <Flex align={"center"} justify={"space-evenly"}>
                <Text size={"sm"} c={"var(--mantine-color-gray-6)"}>
                  {t("credit.saturdayAvailability.from")}
                </Text>
                <TimePicker
                  key={form.key("saturdayAvailabilityFrom")}
                  {...form.getInputProps("saturdayAvailabilityFrom")}
                  min={options["saturdayAvailability"]?.min}
                  max={options["saturdayAvailability"]?.max}
                  w={"30%"}
                  disabled={readOnly}
                />
                <Text size={"sm"} c={"var(--mantine-color-gray-6)"}>
                  {t("credit.saturdayAvailability.to")}
                </Text>
                <TimePicker
                  key={form.key("saturdayAvailabilityTo")}
                  {...form.getInputProps("saturdayAvailabilityTo")}
                  min={options["saturdayAvailability"]?.min}
                  max={options["saturdayAvailability"]?.max}
                  w={"30%"}
                  disabled={readOnly}
                />
              </Flex>
            </Box>
          )}
      </SimpleGrid>

      <SimpleGrid cols={4} mt="sm">
        <Select
          label={t("user.nationality.label")}
          data={nationalities}
          key={form.key("nationality")}
          {...form.getInputProps("nationality")}
          clearable={true}
          required={true}
          disabled={readOnly}
        />
        {visibleFields.includes("visaType") && (
          <Select
            label={t("user.visa.label")}
            data={options["visaType"].map((el) => ({
              value: el,
              label: t(`user.visa.options.${el}`),
            }))}
            key={form.key("visaType")}
            {...form.getInputProps("visaType")}
            clearable={true}
            required={true}
            disabled={readOnly}
          />
        )}
        {visibleFields.includes("visaFrom") && (
          <DatePicker
            label={t("user.visa.from")}
            key={form.key("visaFrom")}
            {...form.getInputProps("visaFrom")}
            onChange={(value: Date | null) => {
              form.setFieldValue("visaFrom", value || undefined);
              revalidateEmploymentDatesRef?.current?.(null, value || undefined);
            }}
            maxDate={new Date()}
            required={true}
            disabled={readOnly}
          />
        )}
        {visibleFields.includes("visaTo") && (
          <DatePicker
            label={t("user.visa.to")}
            key={form.key("visaTo")}
            {...form.getInputProps("visaTo")}
            minDate={new Date()}
            disabled={readOnly}
          />
        )}
      </SimpleGrid>

      <SimpleGrid cols={4} mt="sm">
        <Select
          label={t("bankCredit.creditLanguage.label")}
          data={creditLanguageOptions}
          key={form.key("creditLanguage")}
          {...form.getInputProps("creditLanguage")}
          clearable={true}
          disabled={readOnly}
        />
      </SimpleGrid>
    </Box>
  );
};
